import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import processTokenResponse from '@/modules/auth/functions/processTokenResponse';
import useLoginUserMutation from '@/modules/auth/mutations/useLoginUserMutation';
import useRefreshTokenMutation from '@/modules/auth/mutations/useRefreshTokenMutation';
import useAuthUserStore from '@/modules/auth/stores/useAuthUserStore';
import useTokenStore from '@/modules/auth/stores/useTokenStore';
import type { LoginFormValues } from '@/modules/auth/types/login-form-values';
import config from '@/modules/config/functions/config';
import {
  AuthStartupExpiredAccessTokenError,
  AuthStartupExpiredRefreshTokenError,
  AuthStartupGeneralError,
  AuthStartupInvalidAccessTokenError,
  AuthStartupInvalidRefreshTokenError,
  AuthStartupNoAccessTokenError,
  AuthStartupNoRefreshTokenError,
  BaseError,
  CoreAuthUnauthorizedError,
  UserGetFetchError,
  UserGetGeneralError,
  UserGetInternalServerError,
  UserGetRequestError,
  UserGetServerConnectionError,
} from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import usePublicWretch from '@/modules/query-client/hooks/usePublicWretch';
import createUseUserQueryOptions from '@/modules/users/functions/createUseUserQueryOptions';
import type { User } from '@/modules/users/types/User';
import { useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';

type LoginMode =
  | {
      type: 'login';
      credentials: LoginFormValues;
    }
  | {
      type: 'restore';
    };

interface LoadTokensResult {
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken?: string;
  refreshTokenExpiresAt?: number;
}

export default function useLoginUser() {
  const queryClient = useQueryClient();
  const wretch = usePublicWretch();
  const { i18n } = useTranslation();
  const loginUserMutation = useLoginUserMutation();
  const { setAccessToken, setRefreshToken, getAccessToken, getRefreshToken } =
    useTokenStore();
  const { setUser, clearUser } = useAuthUserStore();

  /**
   * Get the access token from the store
   * @returns The access token
   */
  const getSavedTokens = useCallback(async () => {
    const maybeAccessToken = getAccessToken();
    const maybeRefreshToken = getRefreshToken();

    const accessTokenLoadResult: Pick<
      LoadTokensResult,
      'accessToken' | 'accessTokenExpiresAt'
    > = match(maybeAccessToken)
      .with(undefined, () => {
        throw new AuthStartupNoAccessTokenError();
      })
      .with('expired', () => {
        throw new AuthStartupExpiredAccessTokenError();
      })
      .with('invalid', () => {
        throw new AuthStartupInvalidAccessTokenError();
      })
      .with(
        { token: P.string, expiresAt: P.number },
        ({ token: accessToken, expiresAt: accessTokenExpiresAt }) => ({
          accessToken,
          accessTokenExpiresAt,
        })
      )
      .exhaustive();

    let refreshTokenLoadResult:
      | Pick<LoadTokensResult, 'refreshToken' | 'refreshTokenExpiresAt'>
      | undefined;
    if (config('features.auth.enableTokenRefreshing') === true) {
      refreshTokenLoadResult = match(maybeRefreshToken)
        .with(undefined, () => {
          throw new AuthStartupNoRefreshTokenError();
        })
        .with('expired', () => {
          throw new AuthStartupExpiredRefreshTokenError();
        })
        .with('invalid', () => {
          throw new AuthStartupInvalidRefreshTokenError();
        })
        .with(
          { token: P.string, expiresAt: P.number },
          ({ token: refreshToken, expiresAt: refreshTokenExpiresAt }) => ({
            refreshToken,
            refreshTokenExpiresAt,
          })
        )
        .exhaustive();
    }

    return {
      accessToken: accessTokenLoadResult.accessToken,
      accessTokenExpiresAt: accessTokenLoadResult.accessTokenExpiresAt,
      refreshToken: refreshTokenLoadResult?.refreshToken,
      refreshTokenExpiresAt: refreshTokenLoadResult?.refreshTokenExpiresAt,
    };
  }, [getAccessToken, getRefreshToken]);

  /**
   * Load the access token from the server
   * @param mode - The login mode
   * @returns The access token and refresh token
   */
  const loginAndGetTokens = useCallback(
    async (credentials: LoginFormValues): Promise<LoadTokensResult> => {
      const response = await loginUserMutation.mutateAsync(credentials);
      return processTokenResponse({
        response,
        InvalidAccessTokenErrorClass: AuthStartupInvalidAccessTokenError,
        InvalidRefreshTokenErrorClass: AuthStartupInvalidRefreshTokenError,
      });
    },
    [loginUserMutation]
  );

  /**
   * Login to get access token or retrive the access token
   * from the store depending on the mode
   * @param mode - The login mode
   * @returns The access token
   */
  const loadAccessToken = useCallback(
    async (mode: LoginMode) => {
      return match(mode)
        .with({ type: 'restore' }, getSavedTokens)
        .with({ type: 'login' }, ({ credentials: c }) => loginAndGetTokens(c))
        .exhaustive();
    },
    [loginAndGetTokens, getSavedTokens]
  );

  const refreshTokenMutation = useRefreshTokenMutation({
    shouldStoreTokens: false,
    retry: false,
  });

  return useCallback(
    async (mode: LoginMode) => {
      try {
        logger.debug(
          'auth.startup',
          `Logging user in using mode: ${JSON.stringify(mode.type)}...`
        );
        let tokens: LoadTokensResult;
        try {
          tokens = await loadAccessToken(mode);
        } catch (error) {
          if (config('features.auth.enableTokenRefreshing') !== true)
            throw error;

          // we can't refresh the token if it's not available
          if (
            error instanceof AuthStartupNoAccessTokenError ||
            error instanceof AuthStartupNoRefreshTokenError ||
            error instanceof AuthStartupExpiredRefreshTokenError ||
            error instanceof AuthStartupInvalidRefreshTokenError
          )
            throw error;

          tokens = await refreshTokenMutation.mutateAsync();
          setAccessToken(tokens.accessToken, tokens.accessTokenExpiresAt);
          if (
            config('features.auth.enableTokenRefreshing') === true &&
            !isNil(tokens.refreshToken) &&
            !isNil(tokens.refreshTokenExpiresAt)
          )
            setRefreshToken(tokens.refreshToken, tokens.refreshTokenExpiresAt);
        }

        logger.debug('auth.startup', 'Fetching user profile...');
        let user: User | undefined;

        try {
          const w = wretch.headers({
            Authorization: `Bearer ${tokens.accessToken}`,
          });
          const queryOptions = createUseUserQueryOptions({
            wretch: w,
            lang: i18n.language,
            useLangInQueryKey: false,
          });

          user = await queryClient.fetchQuery({
            ...queryOptions,
            retry: mode.type === 'login' ? queryOptions.retry : false,
          });
        } catch (error) {
          if (error instanceof CoreAuthUnauthorizedError) {
            logger.debug(
              'auth.startup',
              'Got unauthorized error while fetching user profile, refreshing token...'
            );
            tokens = await refreshTokenMutation.mutateAsync();
            setAccessToken(tokens.accessToken, tokens.accessTokenExpiresAt);
            if (
              config('features.auth.enableTokenRefreshing') === true &&
              !isNil(tokens.refreshToken) &&
              !isNil(tokens.refreshTokenExpiresAt)
            )
              setRefreshToken(
                tokens.refreshToken,
                tokens.refreshTokenExpiresAt
              );
            throw new Error('REFRESHING_IN_PROGRESS');
          } else {
            throw error;
          }
        }

        logger.debug('auth.startup', 'User profile fetched successfully.');
        logger.debug('auth.startup', 'Setting authentication state...');
        setAccessToken(tokens.accessToken, tokens.accessTokenExpiresAt);
        if (
          config('features.auth.enableTokenRefreshing') === true &&
          !isNil(tokens.refreshToken) &&
          !isNil(tokens.refreshTokenExpiresAt)
        )
          setRefreshToken(tokens.refreshToken, tokens.refreshTokenExpiresAt);
        setUser(user);
        logger.debug('auth.startup', 'Authentication state set successfully.');
        return { tokens, user };
      } catch (error) {
        logger.debug(
          'auth.startup',
          'Something is not right. Clearing authentication state...'
        );
        clearUser();
        if (error instanceof AuthStartupNoAccessTokenError) {
          logger.debug('auth.startup', error);
          throw error;
        } else if (
          [
            UserGetRequestError,
            CoreAuthUnauthorizedError,
            UserGetServerConnectionError,
            UserGetInternalServerError,
            UserGetFetchError,
            UserGetGeneralError,
          ].some((e) => error instanceof e)
        ) {
          // don't log the error since it's already logged after API call
          throw error;
        } else if (error instanceof BaseError) {
          logger.error('auth.startup', error);
          throw error;
        } else {
          logger.error('auth.startup', error);
          throw new AuthStartupGeneralError(makeErrorFromUnknown(error));
        }
      }
    },
    [
      clearUser,
      i18n.language,
      loadAccessToken,
      queryClient,
      refreshTokenMutation,
      setAccessToken,
      setRefreshToken,
      setUser,
      wretch,
    ]
  );
}
